import { Component, Match, Show, Switch, createSignal } from 'solid-js'
import { toastStore } from '/web/store/toasts'
import TextInput from '/web/shared/TextInput'
import Button from '/web/shared/Button'
import FileInput, { FileInputResult } from '/web/shared/FileInput'
import Divider from '/web/shared/Divider'
import { slugify } from '/common/util'
import { embedApi } from '/web/store/embeddings'
import Select from '/web/shared/Select'
import { getStore } from '/web/store/create'
import { SolidCard } from '/web/shared/Card'
import { pageStore, i18nStore } from '/web/store'
import { createStore } from 'solid-js/store'
import { v4 } from 'uuid'

export { EmbedContent as default }

const EmbedContent: Component = (props) => {
  const i18n = i18nStore((s) => ({ t: s.t }))
  const user = getStore('user')((s) => ({ ui: s.ui }))

  const options = [i18n.t.memory_article + '', i18n.t.memory_pdf + '', i18n.t.memory_text_file + '', i18n.t.memory_plain_text + '']
  const [store, setStore] = createStore({
    wiki: '',
    embedName: '',
    embedText: '',
    type: options[0],
  })

  const [loading, setLoading] = createSignal(false)
  const [file, setFile] = createSignal<File>()

  const embedWiki = async () => {
    setLoading(true)
    try {
      await embedApi.embedArticle(store.wiki)
      toastStore.success(i18n.t.memory_embedding_created + '')
      setLoading(false)
    } finally {
      setLoading(false)
    }
  }

  const embedFile = async () => {
    if (!store.embedName) {
      toastStore.error(i18n.t.memory_enter_name + '')
    }

    setLoading(true)
    try {
      const docNeeded = store.type === i18n.t.memory_pdf + '' || store.type === i18n.t.memory_text_file + ''
      const doc = file()
      if (!doc && docNeeded) {
        toastStore.error(i18n.t.memory_no_pdf + '')
        return
      }

      switch (store.type) {
        case i18n.t.memory_pdf + '':
          await embedApi.embedPdf(v4(), store.embedName, doc!)
          break

        case i18n.t.memory_text_file + '':
          await embedApi.embedFile(v4(), store.embedName, doc!)
          break

        case i18n.t.memory_plain_text + '': {
          if (!store.embedText) {
            toastStore.warn(i18n.t.memory_content_empty + '')
            return
          }
          await embedApi.embedPlainText(v4(), store.embedName, store.embedText)
          break
        }
      }
      toastStore.success(i18n.t.memory_embedding_created + '')
    } finally {
      setLoading(false)
    }
  }

  const onFile = (files: FileInputResult[]) => {
    const file = files[0]
    if (!file) {
      setFile()
      setStore('embedName', '')
      return
    }

    setFile(() => file.file)
    const dot = file.file.name.lastIndexOf('.')
    const name = dot > -1 ? file.file.name.slice(0, dot) : file.file.name
    setStore('embedName', slugify(name))
  }

  return (
    <form class="flex flex-col gap-2">
      <Show when={!user.ui.embeddingModel}>
        <SolidCard bg="premium-700">
          {i18n.t.memory_enable_embeddings_helper.split('Embeddings/Long-Term Memory')[0]}
          <b class="underline hover:cursor-pointer" onClick={() => pageStore.settings(true)}>
            Embeddings/Long-Term Memory
          </b>{' '}
          {i18n.t.memory_enable_embeddings_helper.split('Embeddings/Long-Term Memory')[1]}
        </SolidCard>
      </Show>

      <Select
        items={options.map((value) => ({ label: `${i18n.t.chat_embedding}: ${value}`, value }))}
        fieldName="embed-type"
        value={store.type}
        onChange={(ev) => setStore('type', ev.value)}
      />

      <Switch>
        <Match when={store.type === i18n.t.memory_article + ''}>
          <TextInput
            label={i18n.t.memory_embed_wiki}
            helperText={i18n.t.memory_embed_wiki_helper}
            placeholder={i18n.t.memory_wiki_placeholder}
            value={store.wiki}
            onChange={(ev) => setStore('wiki', ev.currentTarget.value)}
          />
          <Button class="mt-2 w-fit" disabled={loading()} onClick={embedWiki}>
            {i18n.t.memory_embed_article_button}
          </Button>
        </Match>

        <Match when={store.type === i18n.t.memory_pdf + ''}>
          <TextInput
            fieldName="embedName"
            label={i18n.t.admin_name}
            helperText={i18n.t.memory_embed_name_helper}
            value={store.embedName}
            onChange={(ev) => setStore('embedName', ev.currentTarget.value)}
          />

          <FileInput
            fieldName="pdf"
            label={i18n.t.memory_embed_pdf}
            onUpdate={onFile}
            helperText={i18n.t.memory_embed_pdf_helper}
            accept="application/pdf"
          />
          <Button class="mt-2 w-fit" disabled={loading() || !file()} onClick={embedFile}>
            {i18n.t.memory_embed_pdf}
          </Button>
        </Match>

        <Match when={store.type === i18n.t.memory_text_file + ''}>
          <TextInput
            fieldName="embedName"
            label={i18n.t.admin_name}
            helperText={i18n.t.memory_embed_name_optional_helper}
            value={store.embedName}
            onChange={(ev) => setStore('embedName', ev.currentTarget.value)}
          />

          <FileInput
            fieldName="pdf"
            label={i18n.t.memory_embed_file}
            onUpdate={onFile}
            helperText={i18n.t.memory_embed_file_helper}
            accept="text/plain"
          />
          <Button class="mt-2 w-fit" disabled={loading() || !file()} onClick={embedFile}>
            {i18n.t.memory_embed_file}
          </Button>
        </Match>

        <Match when={store.type === i18n.t.memory_plain_text + ''}>
          <TextInput
            fieldName="embedName"
            label={i18n.t.admin_name}
            helperText={i18n.t.memory_embed_name_slug_helper}
            value={store.embedName}
            onChange={(ev) => setStore('embedName', ev.currentTarget.value)}
          />

          <TextInput
            fieldName="embedText"
            label={i18n.t.admin_content}
            helperText={i18n.t.memory_embed_text_helper}
            isMultiline
            onChange={(ev) => setStore('embedText', ev.currentTarget.value)}
            class="max-h-80"
          />

          <Button class="mt-2 w-fit" onClick={embedFile}>
            {i18n.t.memory_embed_text_button}
          </Button>
        </Match>
      </Switch>

      <Divider />
    </form>
  )
}
